import React, { useState, useEffect } from 'react';
import { WifiOff, Wifi } from 'lucide-react';
import { addNetworkStatusListeners, isOffline } from '../../utils/serviceWorker';

interface NetworkStatusProps {
  showOnlineStatus?: boolean;
  className?: string;
}

export const NetworkStatus: React.FC<NetworkStatusProps> = ({
  showOnlineStatus = false,
  className = '',
}) => {
  const [isOnline, setIsOnline] = useState(!isOffline());
  const [showStatus, setShowStatus] = useState(false);
  const onlineHideTimerRef = React.useRef<number | null>(null);

  useEffect(() => {
    const cleanup = addNetworkStatusListeners(
      () => {
        setIsOnline(true);
        setShowStatus(true);
        // Hide online status after 3 seconds
        if (onlineHideTimerRef.current !== null) {
          window.clearTimeout(onlineHideTimerRef.current);
        }
        onlineHideTimerRef.current = window.setTimeout(() => setShowStatus(false), 3000);
      },
      () => {
        setIsOnline(false);
        setShowStatus(true);
      },
    );

    return () => {
      cleanup();
      if (onlineHideTimerRef.current !== null) {
        window.clearTimeout(onlineHideTimerRef.current);
      }
    };
  }, []);

  // Don't show anything if online and not configured to show online status
  if (isOnline && !showOnlineStatus && !showStatus) {
    return null;
  }

  // Don't show anything if offline status is not visible
  if (!showStatus && isOnline) {
    return null;
  }

  return (
    <div
      className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-2 rounded-lg shadow-lg transition-all duration-300 ${
        isOnline ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
      } ${className}`}
    >
      {isOnline ? (
        <>
          <Wifi className="w-4 h-4" />
          <span className="text-sm font-medium">Back online</span>
        </>
      ) : (
        <>
          <WifiOff className="w-4 h-4" />
          <span className="text-sm font-medium">You're offline</span>
        </>
      )}
    </div>
  );
};

export default NetworkStatus;
